import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

/**
 * Stops one bad record blanking the whole app.
 *
 * React unmounts the entire tree when a render throws, so a single document
 * the UI didn't expect — a date stored as a string, a field a migration
 * missed — takes the page to white with nothing on screen and nothing in
 * front of the user to act on.
 *
 * It is worse than it sounds, because Firestore renders from its local cache
 * first. A crash on cached data happens *before* the listener's server
 * response lands, so the page dies, reloads, renders the same stale document
 * and dies again. The data can already be fixed on the server and the app will
 * never get far enough to notice. That is exactly the loop this breaks:
 * reloading clears the boundary and the second pass reaches the server.
 */
interface State {
  error: Error | null
}

export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[encore] render failed', error, info.componentStack)
  }

  private reset = () => {
    this.setState({ error: null })
  }

  private hardReload = async () => {
    // The cache is the usual culprit, so offer to drop it. Firestore rebuilds
    // it from the server on the next listen.
    try {
      const databases = await indexedDB.databases()
      await Promise.all(
        databases
          .filter((entry) => /firestore/i.test(entry.name ?? ''))
          .map(
            (entry) =>
              new Promise<void>((resolve) => {
                const request = indexedDB.deleteDatabase(entry.name!)
                request.onsuccess = () => resolve()
                request.onerror = () => resolve()
                request.onblocked = () => resolve()
              }),
          ),
      )
    } catch {
      /* older browsers have no databases() — the reload alone often does it */
    }
    window.location.reload()
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '60dvh', padding: 24 }}>
        <div className="panel panel-pad stack" style={{ maxWidth: 460, padding: 28, gap: 12 }}>
          <span className="t-eyebrow">Something broke</span>
          <h1 className="t-display-s">This page didn't load</h1>
          <p className="t-caption">
            Something on this page couldn't be read. Nothing has been lost — the rest of
            Encore still works.
          </p>
          <p className="t-spec-caption faint" style={{ wordBreak: 'break-word' }}>
            {this.state.error.message}
          </p>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={this.reset}>
              Try again
            </button>
            <button className="btn btn-primary" onClick={this.hardReload}>
              Reload and clear cache
            </button>
          </div>
        </div>
      </div>
    )
  }
}
